import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLocalStorage from "@shared/hooks/useLocalStorage";
import {
  canPromptInstall,
  promptInstall,
  onInstallableChange,
  isStandalone,
  isIOSSafari,
  isNativeApp,
} from "@shared/push/installPrompt";
import { fetchServerState } from "@shared/push/pushService";
import {
  isNativePushAvailable,
  nativePushReady,
  nativePermission,
  registeredToken,
  enableNativePush,
} from "@shared/push/nativePush";
import "./InstallBanner.css";

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * Store app: the app is installed already, so the line offers what installing
 * is for on the web — reminders, switched on with one tap (the system asks
 * for permission). Shown until this device has them, or the patient declines.
 */
function ReminderBanner() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useLocalStorage("native-reminder-banner-dismissed", false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (dismissed || registeredToken()) return undefined;
    let cancelled = false;
    // Only where it can work: a store build with push, the backend sends native
    // push, and the system has not blocked it.
    nativePushReady()
      .then(() => (isNativePushAvailable() ? Promise.all([fetchServerState(), nativePermission()]) : [null, "denied"]))
      .then(([state, permission]) => {
        if (!cancelled) setReady(state?.native_configured === true && permission !== "denied");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  if (dismissed || !ready || registeredToken()) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await enableNativePush({ reminderHour: 18 });
      setReady(false);
    } catch (error) {
      // Declined in the system dialog: settings still have the switch.
      setDismissed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="install-banner" role="note">
      <span className="install-banner-text">{t("push.banner_reminders")}</span>
      <button type="button" className="install-banner-btn" onClick={enable} disabled={busy}>
        {t("push.banner_enable")}
      </button>
      <button
        type="button"
        className="install-banner-close"
        onClick={() => setDismissed(true)}
        aria-label={t("push.banner_dismiss")}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

/**
 * One line above the program list, until the portal is installed or the
 * patient waves it away: install with one tap where the browser allows it
 * (Chrome, Edge), the steps where it does not (iOS Safari). In the store app
 * it offers reminders instead.
 */
export function InstallBanner({ app = "sophyapp" }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useLocalStorage("pwa-install-dismissed", false);
  const [installable, setInstallable] = useState(canPromptInstall());

  useEffect(() => onInstallableChange(setInstallable), []);

  if (isNativeApp()) return <ReminderBanner />;
  if (dismissed || isStandalone()) return null;
  if (!installable && !isIOSSafari()) return null;

  return (
    <div className="install-banner" role="note">
      <span className="install-banner-text">{t("push.banner_text", { app })}</span>
      {installable ? (
        <button type="button" className="install-banner-btn" onClick={() => promptInstall()}>
          {t("push.banner_install")}
        </button>
      ) : (
        <Link to="/preferences/notifications" className="install-banner-btn">
          {t("push.banner_how")}
        </Link>
      )}
      <button
        type="button"
        className="install-banner-close"
        onClick={() => setDismissed(true)}
        aria-label={t("push.banner_dismiss")}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export default InstallBanner;
